import { Component, ElementRef, Inject, OnInit, Optional, ViewChild } from '@angular/core';
import { SharedModule } from '../../../shared/shared.module';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { FormValidators } from '@edtech-ui/core/utils/form-validators';
import { ClassroomManagementService } from '@edtech-ui/core/services/classroom-management-service/classroom-management.service';
import { Subscription } from 'rxjs';
import { UpdateClassroom } from '@edtech-ui/core/models/Classroom/edit-classroom';
import { tap } from 'rxjs/operators';
import { CoverImageResponse } from '@edtech-ui/core/models/Classroom/cover-image';

@Component({
	selector: 'app-teacher-createclassroom',
	standalone: true,
	imports: [SharedModule, MatDialogModule, MatIconModule],
	templateUrl: './teacher-createclassroom.component.html',
	styleUrl: './teacher-createclassroom.component.scss'
})
export class TeacherCreateclassroomComponent implements OnInit {

	createClassroomForm: FormGroup
	coverImages: string[] = [];
	selectedFileUrl: string | null = null;
	subscription = new Subscription();
	isCreating: boolean = false;
	showTabs = false;

	@ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;


	constructor(
		private _toastr: ToastrService,
		@Optional() private dialogRef: MatDialogRef<TeacherCreateclassroomComponent> | null,
		@Optional() @Inject(MAT_DIALOG_DATA) public data: { mode: 'create' | 'edit', classroom?: UpdateClassroom },
		private router: Router,
		private _classroomService: ClassroomManagementService) {

		this.createClassroomForm = new FormGroup({
			name: new FormControl('', FormValidators.classroomNameValidators),
			subject: new FormControl('', FormValidators.subjectNameValidators),
			grade_level: new FormControl('', FormValidators.gradeLevelValidators),
			cover_image_url: new FormControl(null),
			selected_image_url: new FormControl('')
		})
	}

	ngOnInit(): void {
		if (this.data?.mode === 'edit' && this.data.classroom) {
			const classroom = this.data.classroom;
			this.createClassroomForm.patchValue({
				name: classroom.name,
				subject: classroom.subject,
				grade_level: classroom.grade_level,
				cover_image_url: classroom.cover_image_url,
				selected_image_url: classroom.cover_image_url || ''
			});
			this.selectedFileUrl = classroom.cover_image_url;
		}
		this.getCoverImage();
	}


	onCreateUpClass(): void {
		this.isCreating = true;

		const coverControl = this.createClassroomForm.get('cover_image_url');
		const selectedControl = this.createClassroomForm.get('selected_image_url');

		let coverImageValue = coverControl?.value;
		let selectedImageValue = selectedControl?.value;

		if (!coverImageValue && this.coverImages.length > 0) {
			const defaultImage = this.coverImages[0];
			coverControl?.setValue(defaultImage);
			selectedControl?.setValue(defaultImage);
			this.selectedFileUrl = defaultImage;

			coverImageValue = defaultImage;
			selectedImageValue = defaultImage;
		}

		const payload = new FormData();
		['name', 'subject', 'grade_level'].forEach(field => {
			const value = this.createClassroomForm.get(field)?.value?.trim() || '';
			payload.append(field, value);
		});

		if (coverImageValue instanceof File) {
			payload.append('cover_image', coverImageValue);
		} else if (typeof coverImageValue === 'string') {
			payload.append('cover_image_url', coverImageValue);
		}

		if (typeof selectedImageValue === 'string') {
			payload.append('selected_image_url', selectedImageValue);
		}

		if (this.data?.mode === 'edit' && this.data.classroom?.id) {
			this.subscription.add(
				this._classroomService.updateClassroom(this.data.classroom.id.toString(), payload)
					.pipe(
						tap(response => {
							this.isCreating = false;
							if (response.Success && response.Data) {
								this._toastr.success('Classroom updated successfully!', 'Update Complete');
								this.closeDialog();
							} else {
								this._toastr.error('not updated');
							}
						})
					).subscribe()
			);
		}
		else {
			this.subscription.add(
				this._classroomService.postCreateClassroom(payload).pipe(
					tap(response => {
						this.isCreating = false;
						if (response?.Success && response?.Data) {
							this._toastr.success(response.Message);
							this.closeDialog();
							this.router.navigate(['teacher-dashboard/classroom']);
						} else {
							this._toastr.error(response.Message);
						}
					})
				).subscribe()
			);
		}
	}


	closeDialog(): void {
		this.dialogRef?.close();
	}


	getCoverImage(): void {
		this.subscription.add(
			this._classroomService.getCoverImage().subscribe({
				next: (response: CoverImageResponse) => {
					if (response.Success) {
						this.coverImages = response.Data.image_urls;
					}
				}
			})
		);
	}

	toggleTabs() {
		this.showTabs = true;
	}


	onFileSelected(event: Event): void {
		const input = event.target as HTMLInputElement;
		if (input.files && input.files[0]) {
			const file = input.files[0];
			this.createClassroomForm.patchValue({ cover_image_url: file, selected_image_url: 'uploaded-from-device' });

			if (file.size > 1024 * 1024) {
				this._toastr.error('Image size must be less than 1MB. Please upload another image.');
				input.value = '';
				return;
			}

			const reader = new FileReader();
			reader.onload = () => {
				this.selectedFileUrl = reader.result as string;
				this.showTabs = false;
			};
			reader.readAsDataURL(file);
		}
	}

	selectCoverImage(imageUrl: string): void {
		this.createClassroomForm.patchValue({ cover_image_url: imageUrl, selected_image_url: imageUrl });
		this.selectedFileUrl = imageUrl;
		this.showTabs = false;
	}


	cancelSelectedImage(): void {
		this.selectedFileUrl = null;
		this.createClassroomForm.get('cover_image_url')?.reset();
		this.createClassroomForm.get('selected_image_url')?.reset();
	}

	get encodedSelectedFileUrl(): string | null {
		return this.selectedFileUrl ? encodeURI(this.selectedFileUrl) : null;
	}

	ngOnDestroy() {
		this.subscription.unsubscribe();
	}

}
